import { sourceFromDataset, trackViewItemList } from "../helpers/dataLayer";

const MAX_BATCH_SIZE = 12;
const MAX_BATCH_WAIT = 1000;
const CARD_SELECTOR = "[data-product-card][data-dl-product-id]";

/**
 * Pushes dl_view_item_list for the cards in a grid as each first comes into
 * view, batched. Covers cards added later, by load more or filtering.
 *
 * @param {HTMLElement} grid
 * @returns {function} stops tracking, pushing anything still batched
 */
export default function trackGridImpressions(grid) {
  const observed = new WeakSet();
  let batch = [];
  let timer = null;

  const flush = () => {
    clearTimeout(timer);
    timer = null;
    trackViewItemList(batch);
    batch = [];
  };

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        if (!isIntersecting) return;

        intersectionObserver.unobserve(target);

        const position =
          [...grid.querySelectorAll(CARD_SELECTOR)].indexOf(target) + 1;

        batch.push({ source: sourceFromDataset(target.dataset), position });

        if (batch.length >= MAX_BATCH_SIZE) flush();
        else if (!timer) timer = setTimeout(flush, MAX_BATCH_WAIT);
      });
    },
    { threshold: 0.5 },
  );

  const observeCards = () => {
    grid.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (observed.has(card)) return;
      observed.add(card);
      intersectionObserver.observe(card);
    });
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach(({ removedNodes }) =>
      removedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          intersectionObserver.unobserve(node);
        }
      }),
    );
    observeCards();
  });

  // Don't lose a pending batch to a click through to a product.
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden" && batch.length) flush();
  };

  observeCards();
  mutationObserver.observe(grid, { childList: true });
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    if (batch.length) flush();
    intersectionObserver.disconnect();
    mutationObserver.disconnect();
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
